import { environment } from 'src/environments/environment';

import { Language, Organization } from '../interface/rich-snippet.interface';

export abstract class BaseRichSnippetAdapter {
  protected readonly THUMBNAIL_FORMATS = ['3_2_768', '3_2_568', '8_3_1920'];
  protected readonly LANGUAGE_MAP: Record<string, string> = {
    en: 'English',
    de: 'German',
  };

  protected truncateHeadline(title: string, maxLength: number = 110): string {
    if (title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength - 3) + '...';
  }

  protected extractDescription(content: string, maxLength: number = 200): string | undefined {
    const stripped = this.stripHtml(content);
    if (!stripped) {
      return undefined;
    }
    if (stripped.length <= maxLength) {
      return stripped;
    }
    return stripped.substring(0, maxLength - 3) + '...';
  }

  protected stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
  }

  protected generateThumbnailsFromImage(imageId: string | undefined): string[] {
    if (!imageId) {
      return [];
    }

    return this.THUMBNAIL_FORMATS.map((format) => `${environment.mediaDeliveryUrl}/${imageId}/${format}`);
  }

  protected mapLanguageCodes(langCodes: string[]): Language[] {
    if (!langCodes || langCodes.length === 0) {
      return this.getDefaultLanguages();
    }

    const uniqueLangs = [...new Set(langCodes)];

    const mappedLanguages = uniqueLangs
      .map((lang) => {
        const langName = this.LANGUAGE_MAP[lang];
        if (!langName) {
          return null;
        }
        return {
          '@type': 'Language' as const,
          name: langName,
        };
      })
      .filter((lang): lang is Language => lang !== null);

    return mappedLanguages.length > 0 ? mappedLanguages : this.getDefaultLanguages();
  }

  protected getDefaultLanguages(): Language[] {
    return [
      { '@type': 'Language', name: 'English' },
      { '@type': 'Language', name: 'German' },
    ];
  }

  protected getDefaultAuthor(): Organization[] {
    return [
      {
        '@type': 'Organization',
        name: 'Mercedes-Benz Media',
        url: environment.frontendUrl,
      },
    ];
  }
}
